import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { useStore } from '../context/StoreContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog';
import {
  ArrowLeft, Package, Search, Loader2, ShieldAlert, FileText, Calendar
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

const ReceiveHistory = () => {
  const { storeName } = useStore();
  const navigate = useNavigate();
  const { token } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [storeFilter, setStoreFilter] = useState('all');
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/stock-receipts?limit=2000`, auth)
      .then(res => setReceipts(res.data || []))
      .catch(() => toast.error('Failed to load receipts'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return receipts.filter(r => {
      if (storeFilter !== 'all' && r.store !== storeFilter) return false;
      if (!q) return true;
      return (
        (r.supplier_name || '').toLowerCase().includes(q) ||
        (r.invoice_number || '').toLowerCase().includes(q) ||
        (r.received_by || '').toLowerCase().includes(q) ||
        (r.lines || []).some(l => (l.name || '').toLowerCase().includes(q))
      );
    });
  }, [receipts, search, storeFilter]);

  const totalsAll = useMemo(() => ({
    count: filtered.length,
    spend: filtered.reduce((s, r) => s + (r.total_amount || 0), 0),
    units: filtered.reduce((s, r) => s + (r.lines || []).reduce((sl, l) => sl + (l.quantity || 0), 0), 0),
    overrides: filtered.reduce((s, r) => s + (r.lines || []).filter(l => l.markup_override).length, 0),
  }), [filtered]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="receive-history-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/smart-po')} data-testid="rh-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">Receive History</h1>
            <p className="text-slate-500 mt-1 text-sm">Every stock receipt processed (direct receive — no PO)</p>
          </div>
        </div>
        <Button onClick={() => navigate('/receive-stock')} className="bg-emerald-600 hover:bg-emerald-700" data-testid="rh-new-receipt">
          <Package className="w-4 h-4 mr-2" /> New Receipt
        </Button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <p className="text-xs uppercase text-slate-500">Receipts</p>
          <p className="text-2xl font-bold">{totalsAll.count}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs uppercase text-slate-500">Total Spend</p>
          <p className="text-2xl font-bold text-emerald-700">{fmt(totalsAll.spend)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs uppercase text-slate-500">Units Received</p>
          <p className="text-2xl font-bold">{totalsAll.units}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs uppercase text-slate-500">Markup Overrides</p>
          <p className={`text-2xl font-bold ${totalsAll.overrides > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{totalsAll.overrides}</p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px] flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search supplier, invoice #, staff, or product…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="rh-search"
            />
          </div>
          <select
            value={storeFilter}
            onChange={e => setStoreFilter(e.target.value)}
            className="border border-slate-200 rounded-md px-3 py-2 text-sm h-10"
            data-testid="rh-store-filter"
          >
            <option value="all">All stores</option>
            <option value="orewa">{storeName('orewa')}</option>
            <option value="eastridge">{storeName('eastridge')}</option>
          </select>
        </CardContent>
      </Card>

      {/* Receipts table */}
      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">{receipts.length === 0 ? 'No receipts yet — create your first via "Receive Stock"' : 'No receipts match your filters'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left p-3">Date</th>
                    <th className="text-left p-3">Supplier</th>
                    <th className="text-left p-3">Invoice #</th>
                    <th className="text-left p-3">Store</th>
                    <th className="text-right p-3">Items</th>
                    <th className="text-right p-3">Units</th>
                    <th className="text-right p-3">Total</th>
                    <th className="text-center p-3">Overrides</th>
                    <th className="text-left p-3">Received By</th>
                    <th className="w-16" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => {
                    const units = (r.lines || []).reduce((s, l) => s + (l.quantity || 0), 0);
                    const overrides = (r.lines || []).filter(l => l.markup_override).length;
                    return (
                      <tr
                        key={r.id}
                        className="border-t hover:bg-slate-50 cursor-pointer"
                        onClick={() => setDetail(r)}
                        data-testid={`rh-row-${r.id}`}
                      >
                        <td className="p-3 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-slate-700">
                            <Calendar className="w-3.5 h-3.5 text-slate-400" />
                            {formatDate(r.created_at)}
                          </div>
                          <div className="text-[10px] text-slate-400">{new Date(r.created_at).toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit' })}</div>
                        </td>
                        <td className="p-3 font-medium">{r.supplier_name}</td>
                        <td className="p-3 text-slate-600">{r.invoice_number || '—'}</td>
                        <td className="p-3 uppercase text-xs">{r.store}</td>
                        <td className="p-3 text-right">{(r.lines || []).length}</td>
                        <td className="p-3 text-right">{units}</td>
                        <td className="p-3 text-right font-semibold">{fmt(r.total_amount)}</td>
                        <td className="p-3 text-center">
                          {overrides > 0 ? (
                            <Badge className="bg-amber-100 text-amber-800"><ShieldAlert className="w-3 h-3 mr-0.5" />{overrides}</Badge>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="p-3 text-slate-600">{r.received_by || '—'}</td>
                        <td className="p-3 text-center">
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); setDetail(r); }}>View</Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-emerald-600" /> Receipt — {detail.supplier_name}
                </DialogTitle>
                <DialogDescription>
                  {detail.invoice_number ? `Invoice #${detail.invoice_number} • ` : ''}
                  {formatDateTime(detail.created_at)}
                  {' • '}{storeName(detail.store)}
                  {' • By '}<b>{detail.received_by || 'Unknown'}</b>
                </DialogDescription>
              </DialogHeader>

              <div className={`grid ${(detail.freight || detail.cc_surcharge) ? 'grid-cols-5' : 'grid-cols-3'} gap-3 mb-3`}>
                <div className="bg-slate-50 rounded p-3">
                  <p className="text-xs text-slate-500 uppercase">Net</p>
                  <p className="font-bold">{fmt(detail.subtotal)}</p>
                </div>
                <div className="bg-slate-50 rounded p-3">
                  <p className="text-xs text-slate-500 uppercase">GST</p>
                  <p className="font-bold">{fmt(detail.gst_amount)}</p>
                </div>
                {detail.freight > 0 && (
                  <div className="bg-slate-50 rounded p-3">
                    <p className="text-xs text-slate-500 uppercase">Freight</p>
                    <p className="font-bold">{fmt(detail.freight)}</p>
                  </div>
                )}
                {detail.cc_surcharge > 0 && (
                  <div className="bg-slate-50 rounded p-3">
                    <p className="text-xs text-slate-500 uppercase">CC Surcharge</p>
                    <p className="font-bold">{fmt(detail.cc_surcharge)}</p>
                  </div>
                )}
                <div className="bg-emerald-50 rounded p-3">
                  <p className="text-xs text-emerald-700 uppercase">Total</p>
                  <p className="font-bold text-emerald-700">{fmt(detail.total_amount)}</p>
                </div>
              </div>

              <div className="border rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="text-left p-2 px-3">Product</th>
                      <th className="text-right p-2">Qty</th>
                      <th className="text-right p-2">Cost</th>
                      <th className="text-right p-2">Retail</th>
                      <th className="text-right p-2">Line Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.lines || []).map((l, i) => (
                      <tr key={i} className={`border-t ${l.markup_override ? 'bg-amber-50' : ''}`}>
                        <td className="p-2 px-3">
                          <div className="font-medium">{l.name}</div>
                          <div className="text-[10px] text-slate-400 flex items-center gap-2">
                            {l.barcode || '—'}
                            {l.markup_override && (
                              <Badge className="bg-amber-100 text-amber-800 text-[10px] py-0 px-1.5">
                                <ShieldAlert className="w-3 h-3 mr-0.5" /> {l.override_reason}
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-2 text-right">{l.quantity}</td>
                        <td className="p-2 text-right">{fmt(l.unit_cost)}</td>
                        <td className="p-2 text-right">{fmt(l.sell_price)}</td>
                        <td className="p-2 text-right font-medium">{fmt((l.quantity || 0) * (l.unit_cost || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {detail.invoice_id && (
                <div className="text-xs text-slate-500 mt-2">
                  Linked supplier invoice for payment tracking → <Button variant="link" className="h-auto p-0 text-xs" onClick={() => navigate('/invoices')}>Open Invoices</Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReceiveHistory;
